/* Nirala delivery preference. Shopify shipping settings remain authoritative. */
(() => {
  'use strict';
  if (window.NiralaLocation) return;
  let current = null;
  let mapsPromise = null;
  let arrivalDismissed = false;
  const clean = (value, limit = 500) => String(value || '').trim().slice(0, limit);
  const positionOf = (value) => {
    if (!value) return null;
    const lat = Number(typeof value.lat === 'function' ? value.lat() : value.lat);
    const lng = Number(typeof value.lng === 'function' ? value.lng() : value.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 ? { lat, lng } : null;
  };
  const parseCoordinates = (value) => {
    const parts = clean(value).split(',');
    if (parts.length !== 2 || parts.some((part) => !part.trim())) return null;
    return positionOf({ lat: parts[0], lng: parts[1] });
  };
  const pinUrl = (position) => `https://www.google.com/maps/search/?api=1&query=${position.lat.toFixed(6)},${position.lng.toFixed(6)}`;
  const loadGoogle = (key) => {
    if (window.google?.maps?.importLibrary) return Promise.resolve();
    if (mapsPromise) return mapsPromise;
    mapsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const callback = '__niralaGoogleMapsReady';
      const timer = window.setTimeout(() => fail(), 20000);
      const fail = () => {
        window.clearTimeout(timer);
        script.remove();
        window[callback] = () => {};
        mapsPromise = null;
        reject(new Error('Map unavailable'));
      };
      window[callback] = () => {
        window.clearTimeout(timer);
        if (!window.google?.maps?.importLibrary) { fail(); return; }
        resolve();
      };
      const previousAuthFailure = window.gm_authFailure;
      window.gm_authFailure = () => {
        window.dispatchEvent(new Event('nirala:maps-auth-failure'));
        if (typeof previousAuthFailure === 'function') previousAuthFailure();
      };
      const params = new URLSearchParams({ key, loading: 'async', callback, v: 'weekly', auth_referrer_policy: 'origin' });
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.onerror = fail;
      const nonce = document.querySelector('script[nonce]')?.nonce;
      if (nonce) script.nonce = nonce;
      document.head.append(script);
    });
    return mapsPromise;
  };

  class LocationSelector {
    constructor(root) {
      this.root = root;
      try { this.config = JSON.parse(root.querySelector('[data-location-config]').textContent); }
      catch (_) { return; }
      this.dialog = root.querySelector('dialog');
      this.form = root.querySelector('[data-location-form]');
      this.area = this.form.elements.area;
      this.baseAreaOptions = [...this.area.options].map(option => option.cloneNode(true));
      this.refreshExtraAreas();
      this.address = this.form.elements.address;
      this.link = this.form.elements.map_link;
      this.coordinates = this.form.elements.coordinates;
      this.saveButton = root.querySelector('[data-location-save]');
      this.error = root.querySelector('[data-location-error]');
      this.mapStatus = root.querySelector('[data-location-map-status]');
      this.saved = this.normalize(this.config.attributes || {});
      this.controller = new AbortController();
      this.waiters = [];
      this.mapListeners = [];
      this.hasAreas = this.area.options.length > 1;
      this.destroyed = false;
      this.savePending = false;
      this.geocodeSequence = 0;
      const options = { signal: this.controller.signal };
      window.addEventListener('nirala:maps-auth-failure', () => {
        this.mapAuthFailed = true;
        const panel = this.root.querySelector('[data-location-map-panel]');
        if (panel) panel.hidden = true;
        this.mapStatus.textContent = 'The map is unavailable. Please select your area and enter your address manually.';
      }, options);
      this.form.addEventListener('submit', (event) => { event.preventDefault(); void this.save(); }, options);
      this.dialog.addEventListener('close', () => {
        this.geocodeSequence += 1;
        this.dismissArrival();
        this.resolveWaiters(false);
      }, options);
      this.dialog.addEventListener('cancel', (event) => {
        if (this.savePending) event.preventDefault();
      }, options);
      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-location-close]') && !this.savePending) this.dialog.close();
        if (event.target.closest('[data-location-geolocate]')) this.geolocate();
        if (event.target.closest('[data-location-map-load]')) void this.loadMap();
        if (event.target.closest('[data-location-map-search]')) void this.findAddress();
        if (event.target.closest('[data-location-clear-pin]')) this.clearPin();
      }, options);
      this.form.querySelectorAll('[name="order_type"]').forEach((input) => input.addEventListener('change', () => this.updateMode(), options));
      this.area.addEventListener('change', () => {
        this.clearPin();
        this.address.value = '';
        this.updateMode();
      }, options);
      this.setupAreaList(options);
      this.address.addEventListener('input', () => {
        if (this.pinNeedsAddress) this.geocodeSequence += 1;
        else this.clearPin();
      }, options);
      this.sessionKey = 'nirala-location-activity';
      this.lastActivity = this.readActivity();
      this.needsConfirmation = this.expired();
      this.lastWrite = 0;
      const activity = () => {
        if (document.hidden) return;
        if (this.expired() && !this.needsConfirmation) {
          this.needsConfirmation = true;
          this.updateLabels();
          arrivalDismissed = false;
        }
        if (this.needsConfirmation) {
          if (!this.dialog.open && !document.querySelector('dialog[open]') && this.shouldOpenOnArrival()) this.open();
          return;
        }
        if (this.valid(this.saved)) this.recordActivity();
      };
      ['pointerdown', 'keydown', 'scroll'].forEach(name => document.addEventListener(name, activity, { ...options, passive: true }));
      document.addEventListener('visibilitychange', activity, options);
      window.addEventListener('pageshow', activity, options);
      window.addEventListener('focus', activity, options);
      window.addEventListener('storage', event => {
        if (event.key === this.sessionKey) { this.lastActivity = this.readActivity(); this.needsConfirmation = this.expired(); this.updateLabels(); }
      }, options);
      this.restore();
      if (!this.needsConfirmation && this.valid(this.saved)) this.recordActivity();
      this.scheduleTimer = window.setInterval(() => { this.updateMode(); if (this.expired() && !this.needsConfirmation) { this.needsConfirmation = true; arrivalDismissed = false; this.updateLabels(); } }, 60000);
      this.syncAreaList?.();
      this.updateLabels();
      if (this.shouldOpenOnArrival()) {
        this.openTimer = window.setTimeout(() => {
          this.openTimer = null;
          if (this.shouldOpenOnArrival()) this.open();
        }, 300);
      }
    }

    readActivity() {
      try { return Number(localStorage.getItem('nirala-location-activity')) || 0; }
      catch (_) { return this.lastActivity || 0; }
    }
    expired() {
      const minutes = Number(this.config.sessionMinutes ?? 30);
      return minutes > 0 && (!this.lastActivity || Date.now() - this.lastActivity >= minutes * 60000);
    }
    recordActivity(force = false) {
      this.lastActivity = Date.now();
      if (!force && this.lastActivity - this.lastWrite < 10000) return;
      this.lastWrite = this.lastActivity;
      try { localStorage.setItem(this.sessionKey, String(this.lastActivity)); } catch (_) { /* Memory fallback. */ }
    }
    ready() { return this.valid(this.saved) && !this.needsConfirmation && !this.expired(); }

    setupAreaList(options) {
      const list = this.root.querySelector('[data-location-area-list]');
      if (!list || !this.hasAreas) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'np-location-area-toggle';
      button.id = `AreaToggle-${this.root.dataset.sectionId}`;
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', `AreaList-${this.root.dataset.sectionId}`);
      button.textContent = 'Select area';
      list.id = `AreaList-${this.root.dataset.sectionId}`;
      this.area.classList.add('np-location-native-select');
      this.area.tabIndex = -1;
      this.area.setAttribute('aria-hidden', 'true');
      const picker = document.createElement('div');
      picker.className = 'np-location-area-picker';
      const label = this.area.closest('label');
      label.htmlFor = button.id;
      label.after(picker);
      picker.append(button, list);

      const search = document.createElement('input');
      search.type = 'search';
      search.className = 'np-location-area-search';
      search.placeholder = 'Select area';
      search.setAttribute('aria-label', 'Search delivery areas');
      search.autocomplete = 'off';
      search.spellcheck = false;
      search.setAttribute('enterkeyhint', 'done');
      list.append(search);
      let items = [];
      this.rebuildAreaChoices = () => {
      list.querySelectorAll('button[data-value]').forEach(item => item.remove());
      [...this.area.options].filter(option => option.value).forEach(option => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'np-location-area-option';
        const name = document.createElement('span');
        name.textContent = option.textContent;
        item.append(name);
        if (option.dataset.eta) {
          const eta = document.createElement('small');
          eta.textContent = `~ ${option.dataset.eta}`;
          item.append(eta);
        }
        item.dataset.value = option.value;
        list.append(item);
      });
      items = [...list.querySelectorAll('button[data-value]')];
      };
      this.rebuildAreaChoices();
      const empty = document.createElement('p');
      empty.className = 'np-location-area-empty';
      empty.setAttribute('role', 'status');
      empty.textContent = 'No matching areas. Try another name.';
      empty.hidden = true;
      list.append(empty);
      list.dataset.direction = 'up';
      const viewport = window.visualViewport;
      let positionFrame = 0;
      const updateViewport = () => {
        this.dialog.style.setProperty('--np-location-viewport-height', `${viewport?.height || window.innerHeight}px`);
        this.dialog.style.setProperty('--np-location-viewport-top', `${viewport?.offsetTop || 0}px`);
      };
      const positionList = () => {
        positionFrame = 0;
        if (!this.dialog.open) return;
        updateViewport();
        if (list.hidden) return;
        const box = button.getBoundingClientRect();
        const bounds = this.dialog.getBoundingClientRect();
        const top = Math.max(bounds.top, viewport?.offsetTop || 0) + 8;
        // Keep the list above its trigger and inside the dialog, even with a keyboard open.
        list.style.maxHeight = `${Math.max(0, Math.min(290, box.top - top - 4))}px`;
      };
      const schedulePosition = () => {
        if (this.dialog.open && !positionFrame) positionFrame = window.requestAnimationFrame(positionList);
      };
      this.updateAreaViewport = updateViewport;
      window.addEventListener('resize', schedulePosition, options);
      viewport?.addEventListener('resize', schedulePosition, options);
      viewport?.addEventListener('scroll', schedulePosition, options);
      this.dialog.addEventListener('scroll', schedulePosition, { ...options, passive: true });
      options.signal.addEventListener('abort', () => window.cancelAnimationFrame(positionFrame), { once: true });
      const close = () => {
        list.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        search.value = '';
        items.forEach(item => { item.hidden = false; });
        empty.hidden = true;
      };
      const sync = () => {
        button.textContent = this.area.selectedOptions[0]?.textContent || 'Select area';
        button.setAttribute('aria-label', `Delivery area: ${button.textContent}`);
        items.forEach(item => item.setAttribute('aria-current', item.dataset.value === this.area.value ? 'true' : 'false'));
        button.disabled = this.area.disabled;
        if (button.disabled) close();
        schedulePosition();
      };
      search.addEventListener('input', () => {
        const term = search.value.trim().toLocaleLowerCase();
        items.forEach(item => { item.hidden = !item.textContent.toLocaleLowerCase().includes(term); });
        empty.hidden = items.some(item => !item.hidden);
        list.scrollTop = 0;
      }, options);
      button.addEventListener('click', () => {
        if (!list.hidden) { close(); return; }
        list.hidden = false;
        list.scrollTop = 0;
        button.setAttribute('aria-expanded', 'true');
        positionList();
        // Focus during the tap itself so mobile Safari can open its keyboard.
        search.focus({ preventScroll: true });
        schedulePosition();
      }, options);
      list.addEventListener('click', event => {
        const item = event.target.closest('button[data-value]');
        if (!item) return;
        this.area.value = item.dataset.value;
        this.area.dispatchEvent(new Event('change', { bubbles: true }));
        close(); sync(); button.focus({ preventScroll: true });
      }, options);
      list.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); button.focus({ preventScroll: true }); }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const items = [...list.querySelectorAll('button:not([hidden])')];
          if (!items.length) return;
          const index = items.indexOf(document.activeElement);
          items[index < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1) : (index + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
        }
      }, options);
      search.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); button.focus({ preventScroll: true }); } }, options);
      this.area.addEventListener('change', sync, options);
      this.form.querySelectorAll('[name="order_type"]').forEach(input => input.addEventListener('change', sync, options));
      document.addEventListener('click', event => { if (!list.contains(event.target) && event.target !== button) close(); }, options);
      this.dialog.addEventListener('close', close, options);
      this.syncAreaList = sync;
      sync();
    }

    refreshExtraAreas(excluded) {
      const value = this.area.value;
      this.area.replaceChildren(...this.baseAreaOptions.map(option => option.cloneNode(true)));
      const seen = new Set([...this.area.options].map(option => option.value.toLocaleLowerCase()));
      document.querySelectorAll('template[data-extra-delivery-areas]').forEach(template => {
        if (excluded?.contains(template)) return;
        template.content.querySelectorAll('option').forEach(option => {
          const key = option.value.trim().toLocaleLowerCase();
          if (!key || seen.has(key)) return;
          seen.add(key); this.area.append(option.cloneNode(true));
        });
      });
      this.area.value = value;
      this.hasAreas = this.area.options.length > 1;
      this.rebuildAreaChoices?.();
      this.syncAreaList?.();
    }

    normalize(attributes) {
      const orderType = clean(attributes['Order type'], 20);
      const delivery = orderType === 'Delivery';
      const position = delivery ? parseCoordinates(attributes['Delivery coordinates']) : null;
      const areaName = clean(attributes['Delivery area'], 100);
      const areaOption = [...this.area.options].find((option) => option.value === areaName);
      const country = clean(attributes['Delivery country'] || this.config.deliveryCountry || 'PK', 100).toUpperCase();
      return {
        'Order type': orderType,
        'Delivery city': clean(attributes['Delivery city'], 100),
        'Delivery area': areaName,
        'Delivery address': clean(attributes['Delivery address']),
        'Delivery country': delivery && /^[A-Z]{2}$/.test(country) ? country : '',
        'Delivery province': delivery ? clean(attributes['Delivery province'] || this.config.deliveryProvince, 100) : '',
        'Delivery postal code': delivery ? clean(attributes['Delivery postal code'] || areaOption?.dataset.postcode, 32) : '',
        'Delivery coordinates': position ? `${position.lat.toFixed(6)},${position.lng.toFixed(6)}` : '',
        'Delivery map link': position ? pinUrl(position) : ''
      };
    }

    valid(attributes) {
      if (attributes['Delivery city'] !== this.config.city) return false;
      if (attributes['Order type'] === 'Pickup') return Boolean(this.config.allowPickup && this.pickupAvailable());
      return attributes['Order type'] === 'Delivery' &&
        [...this.area.options].some((option) => option.value && option.value === attributes['Delivery area']) &&
        (!attributes['Delivery coordinates'] || Boolean(parseCoordinates(attributes['Delivery coordinates'])));
    }

    pickupAvailable() {
      if (!this.config.pickupScheduleEnabled) return true;
      const time = value => /^([01]\d|2[0-3]):[0-5]\d$/.test(value || '') ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3)) : null;
      const from = time(this.config.pickupFrom), to = time(this.config.pickupTo);
      if (from === null || to === null || from === to) return false;
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Karachi', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
      const now = Number(parts.find(part => part.type === 'hour')?.value) * 60 + Number(parts.find(part => part.type === 'minute')?.value);
      return from < to ? now >= from && now < to : now >= from || now < to;
    }

    restore() {
      this.pinNeedsAddress = false;
      const mode = this.saved['Order type'] === 'Pickup' && this.config.allowPickup ? 'Pickup' : 'Delivery';
      this.form.querySelectorAll('[name="order_type"]').forEach((input) => { input.checked = input.value === mode; });
      this.area.value = this.saved['Delivery area'];
      this.address.value = this.saved['Delivery address'];
      this.coordinates.value = parseCoordinates(this.saved['Delivery coordinates']) ? this.saved['Delivery coordinates'] : '';
      if (this.marker) this.marker.position = parseCoordinates(this.coordinates.value);
      this.error.hidden = true;
      this.mapStatus.textContent = '';
      this.updateMode();
      this.updatePinLink();
    }

    updateMode() {
      const pickupRadio = this.form.querySelector('[name="order_type"][value="Pickup"]');
      const pickupAllowed = this.pickupAvailable();
      if (pickupRadio) {
        pickupRadio.disabled = !pickupAllowed;
        pickupRadio.closest('label')?.classList.toggle('np-location-mode--unavailable', !pickupAllowed);
        if (pickupRadio.checked && !pickupAllowed) this.form.querySelector('[name="order_type"][value="Delivery"]').checked = true;
      }
      const pickupHours = this.root.querySelector('[data-location-pickup-hours]');
      if (pickupHours) pickupHours.textContent = this.config.pickupScheduleEnabled ? `Pickup: ${this.config.pickupFrom}–${this.config.pickupTo} Pakistan time` : '';
      const schedule = this.root.querySelector('[data-location-pickup-schedule]');
      if (schedule) schedule.textContent = `${pickupAllowed ? 'Pickup available' : 'Pickup closed'} · ${this.config.pickupFrom}–${this.config.pickupTo} Pakistan time`;
      const delivery = this.form.elements.order_type.value !== 'Pickup';
      this.root.querySelector('[data-location-delivery]').hidden = !delivery;
      this.root.querySelector('[data-location-pickup]').hidden = delivery;
      this.root.querySelector('[data-location-unavailable]').hidden = this.hasAreas;
      this.area.required = delivery && !this.syncAreaList;
      this.address.required = false;
      this.area.disabled = !delivery || !this.hasAreas;
      this.address.disabled = !delivery;
      this.link.disabled = !delivery;
      this.saveButton.disabled = this.savePending || (delivery && (!this.hasAreas || !this.area.value));
      const eta = this.root.querySelector('[data-location-eta]');
      eta.textContent = `Estimated delivery: ${this.etaFor('Delivery', this.area.value)}`;
      eta.hidden = !this.hasAreas || !this.area.value;
      const pickupEta = this.root.querySelector('[data-location-pickup-eta]');
      if (pickupEta) pickupEta.textContent = `Estimated pickup: ${this.etaFor('Pickup')}`;
      const areaNotice = this.root.querySelector('[data-location-area-notice]');
      if (areaNotice) {
        const availableAreas = [...this.area.options].map((option) => option.value).filter(Boolean);
        const hasSelectedArea = availableAreas.includes(this.area.value);
        areaNotice.hidden = !delivery || !this.hasAreas || hasSelectedArea;
      }
    }

    etaFor(orderType, areaName = '') {
      if (orderType === 'Pickup') return clean(this.config.pickupEta, 160) || 'Confirmed by our team';
      const option = [...this.area.options].find((item) => item.value && item.value === areaName);
      return clean(option?.dataset.eta, 160) || clean(this.config.defaultEta, 160) || '30–45 min';
    }

    updateLabels() {
      const ready = this.ready();
      const pickup = ready && this.saved['Order type'] === 'Pickup';
      const label = ready ? (pickup ? `Pickup · ${this.config.pickupName}` : `Delivery · ${this.saved['Delivery area']}`) : 'Set your location';
      const destination = pickup ? clean(this.config.pickupName) : this.saved['Delivery area'];
      const detailedLabel = [destination, this.saved['Delivery city']].filter((value, index, values) => value && values.indexOf(value) === index).join(', ');
      document.querySelectorAll('[data-location-label]').forEach((node) => {
        node.textContent = ready ? (node.hasAttribute('data-location-detail') ? detailedLabel : label) : 'Select Location';
      });
      document.querySelectorAll('[data-location-type]').forEach((node) => {
        node.textContent = pickup ? (node.dataset.pickupLabel || 'Pickup from') : (node.dataset.deliveryLabel || 'Delivery to');
      });
      document.querySelectorAll('[data-location-street]').forEach((node) => {
        const address = ready ? (pickup ? clean(this.config.pickupAddress) : this.saved['Delivery address']) : '';
        node.textContent = address;
        node.hidden = !address;
      });
      document.querySelectorAll('[data-location-eta-display]').forEach((node) => {
        const prefix = node.dataset.locationEtaPrefix || (pickup ? 'Estimated pickup' : 'Estimated delivery');
        node.textContent = `${prefix}: ${this.etaFor(pickup ? 'Pickup' : 'Delivery', ready ? this.saved['Delivery area'] : '').replace(/^ETA\s*:\s*/i, '')}`;
        node.hidden = !ready;
      });
      document.querySelectorAll('.np-header-location').forEach((node) => {
        const heading = node.querySelector('[data-location-type]')?.textContent || '';
        const area = node.querySelector('[data-location-label]')?.textContent || '';
        const street = node.querySelector('[data-location-street]')?.textContent || '';
        const eta = node.querySelector('[data-location-eta-display]')?.textContent || '';
        const fullLabel = [heading, area, street, eta].filter(Boolean).join('. ');
        node.setAttribute('aria-label', fullLabel);
        node.setAttribute('title', fullLabel);
      });
      document.querySelectorAll('[data-location-open]').forEach((node) => {
        node.setAttribute('aria-haspopup', 'dialog');
        node.setAttribute('aria-controls', this.dialog.id);
      });
    }

    shouldOpenOnArrival() {
      if (this.destroyed || !this.config.showOnArrival || this.config.designMode || this.ready() || arrivalDismissed) return false;
      if (window.NiralaStoreHours && !window.NiralaStoreHours.canOrder()) return false;
      if (this.needsConfirmation && this.valid(this.saved)) return true;
      try { return sessionStorage.getItem('nirala-location-browsing') !== '1'; }
      catch (_) { return true; }
    }

    dismissArrival() {
      window.clearTimeout(this.openTimer);
      this.openTimer = null;
      arrivalDismissed = true;
      try { sessionStorage.setItem('nirala-location-browsing', '1'); } catch (_) { /* In-memory dismissal still applies on this page. */ }
    }

    open() {
      if (this.destroyed || this.dialog.open) return;
      window.clearTimeout(this.openTimer);
      this.restore();
      this.area.value = '';
      this.address.value = '';
      this.clearPin();
      this.updateMode();
      this.syncAreaList?.();
      const notice = this.root.querySelector('[data-location-reconfirm]');
      if (notice) notice.hidden = !(this.valid(this.saved) && !this.ready());
      this.updateAreaViewport?.();
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
    }

    ensure() {
      if (!this.config.requireLocation || this.ready()) return Promise.resolve(true);
      this.open();
      return new Promise((resolve) => this.waiters.push(resolve));
    }

    getCheckoutAddress() {
      if (this.config.prefillCheckout === false || this.saved['Order type'] !== 'Delivery' || !this.ready()) return null;
      const country = this.saved['Delivery country'];
      if (!/^[A-Z]{2}$/.test(country)) return null;
      const address = {
        address1: this.saved['Delivery address'] || (this.config.prefillArea !== false ? this.saved['Delivery area'] : ''),
        city: this.saved['Delivery city'],
        country
      };
      if (this.config.prefillArea !== false && this.saved['Delivery address'] && this.saved['Delivery area']) address.address2 = this.saved['Delivery area'];
      if (this.saved['Delivery province']) address.province = this.saved['Delivery province'];
      if (this.saved['Delivery postal code']) address.zip = this.saved['Delivery postal code'];
      return address;
    }

    resolveWaiters(value) {
      this.waiters.splice(0).forEach((resolve) => resolve(value));
    }

    async save() {
      if (this.savePending || !this.form.reportValidity()) return;
      const delivery = this.form.elements.order_type.value === 'Delivery';
      const attributes = this.normalize({
        'Order type': delivery ? 'Delivery' : 'Pickup',
        'Delivery city': this.config.city,
        'Delivery area': delivery ? this.area.value : '',
        'Delivery address': delivery ? this.address.value : this.config.pickupAddress,
        'Delivery coordinates': delivery ? this.coordinates.value : ''
      });
      if (!this.valid(attributes)) {
        this.error.textContent = delivery ? 'Please select an available delivery area.' : 'Pickup is unavailable at this time.';
        this.error.hidden = false;
        return;
      }
      this.savePending = true;
      const label = this.saveButton.textContent;
      this.saveButton.textContent = 'Saving location…';
      this.updateMode();
      this.error.hidden = true;
      const timeout = new AbortController();
      const timer = window.setTimeout(() => timeout.abort(), 15000);
      try {
        const routeRoot = window.Shopify?.routes?.root || this.config.cartRoot || '/';
        const save = async () => {
        const response = await fetch(`${routeRoot.replace(/\/?$/, '/')}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin', body: JSON.stringify({ attributes }), signal: timeout.signal
        });
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
        };
        const cart = await (window.Nirala?.mutateCart ? window.Nirala.mutateCart(save) : save());
        if (this.destroyed) return;
        const confirmedAttributes = this.normalize(cart.attributes || {});
        if (!this.valid(confirmedAttributes)) throw new Error('Location was not saved to the cart');
        this.saved = confirmedAttributes;
        this.needsConfirmation = false;
        this.recordActivity(true);
        this.dismissArrival();
        this.updateLabels();
        this.resolveWaiters(true);
        this.dialog.close();
        document.dispatchEvent(new CustomEvent('nirala:location-changed', { detail: { attributes: this.saved, cart } }));
        window.Nirala?.publishCart?.(cart);
      } catch (_) {
        if (!this.destroyed) {
          this.error.textContent = 'We could not save your location. Check your connection and try again.';
          this.error.hidden = false;
        }
      } finally {
        window.clearTimeout(timer);
        this.savePending = false;
        this.saveButton.textContent = label;
        this.updateMode();
      }
    }

    clearPin() {
      this.geocodeSequence += 1;
      this.pinNeedsAddress = false;
      this.coordinates.value = '';
      this.link.value = '';
      if (this.marker) this.marker.position = null;
      this.updatePinLink();
      this.mapStatus.textContent = '';
    }

    updatePinLink() {
      const row = this.root.querySelector('[data-location-pin-row]');
      const position = parseCoordinates(this.coordinates.value);
      this.link.value = position ? pinUrl(position) : '';
      row.hidden = !position;
    }

    setPin(value, preserveAddress = false) {
      const position = positionOf(value);
      if (!position || this.destroyed) return;
      this.geocodeSequence += 1;
      this.coordinates.value = `${position.lat.toFixed(6)},${position.lng.toFixed(6)}`;
      this.link.value = pinUrl(position);
      if (this.marker) this.marker.position = position;
      if (this.map) { this.map.panTo(position); this.map.setZoom(17); }
      this.pinNeedsAddress = !preserveAddress;
      if (!preserveAddress) this.address.value = '';
      this.updatePinLink();
      this.mapStatus.textContent = preserveAddress ? 'Pin set. Check your selected area, address and pin before confirming.' : 'Pin set. Please confirm the area and enter the full street address for this pin.';
    }

    geolocate() {
      const button = this.root.querySelector('[data-location-geolocate]');
      if (!navigator.geolocation || !window.isSecureContext) {
        this.mapStatus.textContent = 'Location sharing is unavailable in this browser. Please type your address.';
        return;
      }
      button.disabled = true;
      const sequence = ++this.geocodeSequence;
      this.mapStatus.textContent = 'Requesting your location…';
      navigator.geolocation.getCurrentPosition((result) => {
        button.disabled = false;
        if (this.destroyed || sequence !== this.geocodeSequence) return;
        this.setPin({ lat: result.coords.latitude, lng: result.coords.longitude });
      }, (error) => {
        button.disabled = false;
        if (this.destroyed) return;
        this.mapStatus.textContent = error.code === 1 ? 'Location permission was not granted. You can type your address instead.' : 'We could not find your location. Please type your address or try again.';
      }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
    }

    async loadMap() {
      if (this.map || !this.config.apiKey || this.mapLoading) return;
      const button = this.root.querySelector('[data-location-map-load]');
      this.mapLoading = true;
      const placeholder = this.root.querySelector('[data-location-map-skeleton]');
      if (placeholder) placeholder.hidden = false;
      button.disabled = true;
      this.mapStatus.textContent = 'Loading Google Maps…';
      try {
        await loadGoogle(this.config.apiKey);
        const [{ Map }, { AdvancedMarkerElement }, { Geocoder }] = await Promise.all([
          google.maps.importLibrary('maps'), google.maps.importLibrary('marker'), google.maps.importLibrary('geocoding')
        ]);
        if (this.destroyed) return;
        if (this.mapAuthFailed) throw new Error('Map authorization failed');
        this.root.querySelector('[data-location-map-panel]').hidden = false;
        const selected = parseCoordinates(this.coordinates.value);
        const center = clean(this.config.latitude) && clean(this.config.longitude) ? positionOf({ lat: this.config.latitude, lng: this.config.longitude }) : null;
        this.map = new Map(this.root.querySelector('[data-location-map]'), {
          center: selected || center || { lat: 0, lng: 0 }, zoom: selected ? 17 : center ? 13 : 2,
          mapId: this.config.mapId || 'DEMO_MAP_ID', streetViewControl: false, mapTypeControl: false,
          fullscreenControl: false, gestureHandling: 'cooperative'
        });
        this.geocoder = new Geocoder();
        this.marker = new AdvancedMarkerElement({ map: this.map, position: selected || undefined, gmpDraggable: true, title: 'Delivery pin. Drag to your entrance.' });
        this.marker.addEventListener('gmp-dragend', () => this.setPin(this.marker.position), { signal: this.controller.signal });
        this.mapListeners.push(this.map.addListener('click', (event) => this.setPin(event.latLng)));
        button.hidden = true;
        this.mapStatus.textContent = 'Find your typed address, use your current location, or click the map to set a pin.';
      } catch (_) {
        if (!this.destroyed) this.mapStatus.textContent = 'The map could not load. You can still select your area and type your address.';
      } finally {
        this.mapLoading = false;
        if (placeholder) placeholder.hidden = true;
        button.disabled = false;
      }
    }

    async findAddress() {
      if (!this.geocoder) return;
      const address = clean(this.address.value);
      if (address.length < 8) {
        this.mapStatus.textContent = 'Enter your full street address first, then choose Find my address.';
        this.address.focus();
        return;
      }
      const sequence = ++this.geocodeSequence;
      this.mapStatus.textContent = 'Finding your address…';
      const city = this.config.city === 'Your city' ? '' : this.config.city;
      try {
        const result = await this.geocoder.geocode({ address: [address, this.area.value, city].filter(Boolean).join(', ') });
        if (this.destroyed || sequence !== this.geocodeSequence) return;
        if (!result.results?.[0]?.geometry?.location) throw new Error('Address not found');
        this.setPin(result.results[0].geometry.location, true);
        this.mapStatus.textContent = 'Suggested pin found. Please check it and move it if necessary; your written address is unchanged.';
      } catch (_) {
        if (!this.destroyed && sequence === this.geocodeSequence) this.mapStatus.textContent = 'No map result was available. Please keep your full address or position the pin manually.';
      }
    }

    destroy() {
      this.destroyed = true;
      this.controller.abort();
      this.resolveWaiters(false);
      window.clearTimeout(this.openTimer);
      window.clearInterval(this.scheduleTimer);
      this.geocodeSequence += 1;
      this.mapListeners.forEach((listener) => listener.remove());
      if (this.marker) this.marker.map = null;
      if (this.dialog.open) this.dialog.close();
    }
  }

  const init = () => {
    const root = document.querySelector('[data-np-location]');
    if (!root) return;
    if (root === current?.root) { current.refreshExtraAreas(); current.updateMode(); current.updateLabels(); return; }
    if (current) current.destroy();
    current = new LocationSelector(root);
  };
  window.NiralaLocation = {
    ensure: () => current?.ensure() || Promise.resolve(true),
    isReady: () => !current || !current.config.requireLocation || current.ready(),
    hasSavedLocation: () => Boolean(current && current.valid(current.saved)),
    open: () => current?.open(),
    getAttributes: () => current ? { ...current.saved } : {},
    getCheckoutAddress: () => current?.getCheckoutAddress() || null
  };
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-location-open]')) { event.preventDefault(); current?.open(); }
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('nirala:cart-updated', () => current?.updateLabels());
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('template[data-extra-delivery-areas]')) { current?.refreshExtraAreas(event.target); current?.updateMode(); }
    if (current && event.target.contains(current.root)) { current.destroy(); current = null; }
  });
  document.addEventListener('shopify:section:select', (event) => {
    if (current && event.target.contains(current.root)) current.open();
  });
  document.addEventListener('shopify:block:select', (event) => {
    if (current && event.target.contains(current.root)) current.open();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
